import React from 'react';
import { Box, Button, Card, CardContent, Chip, IconButton, Stack, Typography, Alert } from '@mui/material';
import { IconRefresh, IconTrash, IconDownload } from '@tabler/icons';
import ReplyOutlinedIcon from '@mui/icons-material/ReplyOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { encodePathSegments } from '../../../../../utils/media-url';

const BACKEND_SERVER = process.env.REACT_APP_BACKEND_SERVER || 'http://127.0.0.1:8000/api/';

export const STATUS_CONFIG = {
    local: { label: 'Não enviado', color: 'default' },
    pending: { label: 'Em análise', color: 'warning' },
    approved: { label: 'Aprovado', color: 'success' },
    rejected: { label: 'Rejeitado', color: 'error' }
};

// Ícones dos botões - a Meta usa QUICK_REPLY/URL/PHONE_NUMBER/COPY_CODE,
// a w-api usa CALL/URL/REPLAY (vocabulário diferente, mesma ideia).
const BUTTON_ICONS = {
    QUICK_REPLY: <ReplyOutlinedIcon fontSize="small" />,
    URL: <OpenInNewOutlinedIcon fontSize="small" />,
    PHONE_NUMBER: <CallOutlinedIcon fontSize="small" />,
    COPY_CODE: <ContentCopyOutlinedIcon fontSize="small" />,
    CALL: <CallOutlinedIcon fontSize="small" />,
    REPLAY: <ReplyOutlinedIcon fontSize="small" />
};

const HEADER_MEDIA_ICONS = {
    IMAGE: <ImageOutlinedIcon />,
    VIDEO: <VideocamOutlinedIcon />,
    DOCUMENT: <DescriptionOutlinedIcon />
};

const HEADER_MEDIA_LABELS = {
    IMAGE: 'Imagem',
    VIDEO: 'Vídeo',
    DOCUMENT: 'Documento'
};

// Prévia estilo WhatsApp de um template - lê `components` no formato da
// Graph API (quando o template vem da Meta) ou os campos locais simples
// (quando o template é local: criado via w_api, ou nunca chegou a existir
// na Meta, ex: falhou ao criar). Reaproveitado em "Templates WhatsApp"
// (lista + prévia ao vivo ao criar) e em "Criar Campanha" (prévia do
// template selecionado).
const TemplatePreviewCard = ({ appId, entry, clientType, onRefreshStatus, onDelete, onImport, localImagePreviewUrl }) => {
    const { meta, local } = entry;
    const name = meta?.name || local?.name;
    const language = meta?.language || local?.language;
    const rawStatus = (meta?.status || local?.status || '').toLowerCase();
    const rejectedReason = meta?.rejected_reason || local?.rejected_reason;

    let header = null;
    let body = '';
    let footer = null;
    let buttons = [];

    if (meta?.components) {
        const headerComp = meta.components.find((c) => c.type === 'HEADER');
        const bodyComp = meta.components.find((c) => c.type === 'BODY');
        const footerComp = meta.components.find((c) => c.type === 'FOOTER');
        const buttonsComp = meta.components.find((c) => c.type === 'BUTTONS');
        header = headerComp || null;
        body = bodyComp?.text || '';
        footer = footerComp?.text || null;
        buttons = buttonsComp?.buttons || [];
    } else if (local) {
        header = local.header_format === 'IMAGE' ? { format: 'IMAGE' } : local.header_text ? { format: 'TEXT', text: local.header_text } : null;
        body = local.body_text || '';
        footer = local.footer_text || null;
        buttons = local.buttons || [];
    }

    // A Meta não devolve a imagem de volta na listagem de templates (só o
    // handle usado na criação) - por isso a prévia SEMPRE usa a cópia
    // própria guardada no R2 (`local.header_image_r2_key`), mesmo quando o
    // resto dos campos (header/body/footer) veio da Meta (`meta.components`,
    // fonte mais atual pra texto). Calculado fora do if/else acima de
    // propósito - sem isso, sempre que `meta.components` existisse a prévia
    // caía direto no ícone genérico de "Imagem", nunca mostrando a foto de
    // verdade mesmo com `header_image_r2_key` disponível.
    const headerImageUrl =
        (header?.format === 'IMAGE' || local?.header_format === 'IMAGE') &&
        (localImagePreviewUrl || (local?.header_image_r2_key ? `${BACKEND_SERVER}v1/apps/${appId}/media/${encodePathSegments(local.header_image_r2_key)}/` : null));

    return (
        <Card sx={{ borderRadius: 4 }}>
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Typography variant="h6" fontWeight="700">
                        {name}
                    </Typography>
                    <Chip
                        size="small"
                        label={STATUS_CONFIG[rawStatus]?.label || rawStatus || '—'}
                        color={STATUS_CONFIG[rawStatus]?.color || 'default'}
                    />
                </Stack>

                {language && (
                    <Typography variant="body2" color="text.secondary" mb={1}>
                        {language}
                    </Typography>
                )}

                {/* Bolha estilo WhatsApp */}
                <Box sx={{ bgcolor: '#d9fdd3', borderRadius: '8px 8px 0 0', p: 1.5 }}>
                    {header?.format === 'TEXT' && (
                        <Typography variant="body2" fontWeight="700" mb={0.5}>
                            {header.text}
                        </Typography>
                    )}
                    {header?.format === 'IMAGE' &&
                        (headerImageUrl ? (
                            <Box
                                component="img"
                                src={headerImageUrl}
                                sx={{ width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 1, mb: 1 }}
                            />
                        ) : (
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                gap={1}
                                sx={{ bgcolor: 'rgba(0,0,0,0.06)', borderRadius: 1, py: 2, mb: 1 }}
                            >
                                {HEADER_MEDIA_ICONS.IMAGE}
                                <Typography variant="caption" color="text.secondary">
                                    {HEADER_MEDIA_LABELS.IMAGE}
                                </Typography>
                            </Box>
                        ))}
                    {header?.format && header.format !== 'TEXT' && header.format !== 'IMAGE' && (
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            gap={1}
                            sx={{ bgcolor: 'rgba(0,0,0,0.06)', borderRadius: 1, py: 2, mb: 1 }}
                        >
                            {HEADER_MEDIA_ICONS[header.format] || <ImageOutlinedIcon />}
                            <Typography variant="caption" color="text.secondary">
                                {HEADER_MEDIA_LABELS[header.format] || header.format}
                            </Typography>
                        </Box>
                    )}
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {body || 'Sua mensagem aparecerá aqui...'}
                    </Typography>
                    {footer && (
                        <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                            {footer}
                        </Typography>
                    )}
                </Box>

                {buttons.length > 0 && (
                    <Box sx={{ border: '1px solid rgba(0,0,0,0.08)', borderTop: 0, borderRadius: '0 0 8px 8px', mb: 1 }}>
                        {buttons.map((btn, i) => (
                            <Box
                                key={i}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                gap={1}
                                sx={{
                                    py: 1,
                                    borderTop: i > 0 ? '1px solid rgba(0,0,0,0.08)' : undefined,
                                    color: '#0091ea'
                                }}
                            >
                                {BUTTON_ICONS[btn.type] || null}
                                <Typography variant="body2">{btn.text || btn.buttonText}</Typography>
                            </Box>
                        ))}
                    </Box>
                )}

                {rawStatus === 'rejected' && rejectedReason && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                        {rejectedReason}
                    </Alert>
                )}

                {local?.id && (onRefreshStatus || onDelete) && (
                    <Stack direction="row" spacing={1} mt={2}>
                        {clientType === 'official' && onRefreshStatus && (
                            <Button size="small" startIcon={<IconRefresh size={16} />} onClick={() => onRefreshStatus(local.id)}>
                                Atualizar status
                            </Button>
                        )}
                        {onDelete && (
                            <IconButton size="small" color="error" onClick={() => onDelete(local.id)}>
                                <IconTrash size={16} />
                            </IconButton>
                        )}
                    </Stack>
                )}

                {!local?.id && meta && onImport && (
                    <Stack direction="row" spacing={1} mt={2}>
                        <Button size="small" startIcon={<IconDownload size={16} />} onClick={() => onImport(meta)}>
                            Importar (criado direto na Meta)
                        </Button>
                    </Stack>
                )}
            </CardContent>
        </Card>
    );
};

export default TemplatePreviewCard;
